import { capitalize, comparatorFromKeyAndRanking, generateCSVContent } from '@airreading/common';
import {
  csvForDeltas,
  deltaKindSchema,
  EdlinkOrgChartDryRunResult,
  EdlinkOrgChartImportSuccess,
  EdlinkOrgChartImportSummary,
} from '@airreading/common/data';
import { CsvColumn, downloadCSV } from './csv-utils';

const DETAILS_EXCLUDE = ['id', 'externalId'];

const detailsToContext = (details?: Record<string, string>): string => {
  if (!details) {
    return '';
  }

  const context = Object.entries(details).flatMap(([k, v]) => (DETAILS_EXCLUDE.includes(k) ? [] : [[k, v]]));
  return JSON.stringify(Object.fromEntries(context));
};

type SummaryRow = Pick<EdlinkOrgChartImportSuccess, 'type' | 'kind' | 'internalId' | 'externalId'> & {
  outcome: string;
  context: string;
};

const SUMMARY_COLUMNS: CsvColumn<SummaryRow>[] = [
  { id: 'Outcome', getValue: (row) => row.outcome },
  { id: 'Entity type', getValue: (row) => capitalize(row.type) },
  { id: 'Internal ID', getValue: (row) => row.internalId ?? null },
  { id: 'External ID', getValue: (row) => row.externalId ?? null },
  { id: 'Action', getValue: (row) => capitalize(row.kind) },
  { id: 'Context', getValue: (row) => row.context },
];

export function exportSummaryToCSV(summary: EdlinkOrgChartImportSummary, filename: string): void {
  const rows: SummaryRow[] = [
    ...summary.items.map((item) => ({ ...item, outcome: 'Success', context: detailsToContext(item.details) })),
    ...summary.failureItems.map((item) => ({ ...item, outcome: 'Failure', context: item.details?.reason ?? '' })),
    ...summary.skipItems.map((item) => ({ ...item, outcome: 'Skip', context: detailsToContext(item.details) })),
  ];

  downloadCSV(generateCSVContent(rows, SUMMARY_COLUMNS), filename);
}

const byDeltaKind = comparatorFromKeyAndRanking(
  (delta: EdlinkOrgChartDryRunResult['items'][number]) => delta.kind,
  deltaKindSchema.options,
);

export function exportDryRunToCSV(result: EdlinkOrgChartDryRunResult, filename: string): void {
  const sortedByAction = [...result.items].sort(byDeltaKind);
  downloadCSV(csvForDeltas(sortedByAction), filename);
}
